/**
 * Design system component: ItemListRow
 *
 * Single horizontal row for the mobile item list. Renders a 46×46px icon
 * block (photo or category emoji fallback), item name with StatusDot and
 * CategoryChip, an owner → borrower chain, and a due-date chip when the
 * current user is the active borrower.
 *
 * Layout behaviour:
 *   - Background highlights on hover (surfaceSubtle) with 120ms transition.
 *   - A border-bottom divider is suppressed on `isLast` rows to avoid a
 *     double-border at the bottom of the list.
 *   - Keyboard-accessible: responds to Enter and Space key presses.
 *   - Borrower avatar (26px) is shown on the right only when someone else
 *     holds the item (the current user already knows it's with them).
 */
import { useState } from 'react';
import { T, CAT_FALLBACK, CATEGORY_EMOJI, RADIUS } from '@/lib/tokens';
import { StatusDot } from './StatusDot';
import { Avatar } from './Avatar';
import { CategoryChip } from './CategoryChip';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import type { ItemWithOwner } from '@/hooks/useItems';
import { canViewBorrowerDetails } from '@/lib/loanVisibility';
import { DueDateBadge, getDueStatus } from './DueDateBadge';

const formatDue = (dateStr: string) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

interface ItemListRowProps {
  item: ItemWithOwner;
  currentUserId: string;
  onClick: () => void;
  isLast?: boolean;
  groupId?: string;
}

/**
 *
 * @param root0
 * @param root0.item
 * @param root0.currentUserId
 * @param root0.onClick
 * @param root0.isLast
 * @param root0.groupId
 */
export function ItemListRow({
  item,
  currentUserId,
  onClick,
  isLast = false,
  groupId,
}: ItemListRowProps) {
  const [hovered, setHovered] = useState(false);

  const catColours = item.category && T.cat[item.category] ? T.cat[item.category] : CAT_FALLBACK;
  const emoji = item.category ? (CATEGORY_EMOJI[item.category] ?? '📦') : '📦';
  const { url: photoThumbUrl } = useSignedUrl(item.photo_url);

  const borrowerIsCurrentUser = item.active_loan?.borrower_id === currentUserId;
  const statusState = !item.active_loan
    ? 'available'
    : borrowerIsCurrentUser
      ? 'with-you'
      : 'on-loan';

  const loanMasked = !canViewBorrowerDetails(item.active_loan, groupId);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        appearance: 'none',
        font: 'inherit',
        textAlign: 'left' as const,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '13px 10px',
        borderRadius: RADIUS.lg,
        cursor: 'pointer',
        transition: 'background 0.12s ease',
        background: hovered ? T.surfaceSubtle : 'transparent',
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: isLast ? 'none' : `1px solid ${T.border}`,
        minHeight: 44,
      }}
    >
      {/* Icon block */}
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: RADIUS.md,
          flexShrink: 0,
          background: photoThumbUrl ? T.surfaceSubtle : catColours.bg,
          border: `1px solid ${T.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {photoThumbUrl ? (
          <img
            src={photoThumbUrl}
            alt={item.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: 11,
              display: 'block',
            }}
          />
        ) : (
          <span style={{ fontSize: 24 }}>{emoji}</span>
        )}
      </div>

      {/* Text block */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Top row: status dot + name + category chip */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <StatusDot state={statusState} />
          <span
            style={{
              fontSize: 15,
              fontWeight: 600,
              color: T.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {item.name}
          </span>
          {item.category && <CategoryChip category={item.category} />}
        </div>

        {/* Bottom row: owner → holder · [due chip] */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            {item.owner_name ?? 'Unknown'}
            {item.active_loan?.borrower_name && !loanMasked && (
              <>
                <span style={{ margin: '0 3px', color: T.border }}>{' \u2192 '}</span>
                <span style={{ color: T.textSec }}>
                  {item.active_loan.borrower_id === currentUserId
                    ? 'You'
                    : item.active_loan.borrower_name}
                </span>
              </>
            )}
            {item.active_loan && loanMasked && (
              <>
                <span style={{ margin: '0 3px', color: T.border }}>{' \u2192 '}</span>
                <span style={{ color: T.textMuted, fontStyle: 'italic' }}>On loan</span>
              </>
            )}
          </span>
          {/* Due date chip: borrower sees future dates + overdue badge; owner sees overdue badge only */}
          {!loanMasked && item.active_loan?.due_at && (
            <>
              {borrowerIsCurrentUser && getDueStatus(item.active_loan.due_at) === 'ok' && (
                <span
                  style={{
                    fontSize: 11,
                    padding: '1px 6px',
                    borderRadius: 4,
                    fontWeight: 600,
                    background: T.surfaceSubtle,
                    color: T.textMuted,
                  }}
                >
                  Due {formatDue(item.active_loan.due_at)}
                </span>
              )}
              {(borrowerIsCurrentUser || item.owner_id === currentUserId) && (
                <DueDateBadge dueAt={item.active_loan.due_at} />
              )}
            </>
          )}
        </div>
      </div>

      {/* Right meta */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 4,
          flexShrink: 0,
        }}
      >
        {item.active_loan &&
          !borrowerIsCurrentUser &&
          !loanMasked &&
          item.active_loan.borrower_name && (
            <Avatar name={item.active_loan.borrower_name} size={26} />
          )}
        <span style={{ color: T.textMuted, fontSize: 16 }}>›</span>
      </div>
    </button>
  );
}
